import { elementType, string } from "prop-types";
import React, { useEffect } from "react";
import { useHistory } from "react-router";
import { Redirect, useLocation } from "react-router-dom";
import useShortCode from "../hooks/useShortCode";
import useUpdateEffect from "../hooks/useUpdateEffect";
import ProtectedRoute from "./ProtectedRoute";
import useUserOrganisations from "../hooks/useUserOrganisations";
import useOrganisation from "../hooks/useOrganisation";
import LoadingMessage from "./LoadingMessage";
import useFetch from "../hooks/useFetch";
import SettingsService from "../services/settings-service";
import useUser from "../hooks/useUser";
import { useFeature } from "./FeatureGate";
import useModuleSubscription from "../hooks/useModuleSubscription";

async function noop() {}

const getModuleSubscriptions = (subscriptionData) => {
  return (
    subscriptionData?.subscription?.moduleSubscriptions ||
    subscriptionData?.moduleSubscriptions ||
    []
  );
};

const getModuleAddonsRedirectPath = ({ moduleName, subscriptionData }) => {
  if (!moduleName) return null;

  const moduleSubscriptions = getModuleSubscriptions(subscriptionData);
  const selectedModule = moduleSubscriptions.find(
    (module) => module?.id === moduleName,
  );

  const moduleTrialExpired = Boolean(
    selectedModule?.trialEndsAt &&
    !selectedModule?.isActive &&
    !selectedModule?.isInTrial &&
    new Date(selectedModule.trialEndsAt) < new Date(),
  );
  const moduleHasPlan = Boolean(selectedModule?.planName);

  if (moduleTrialExpired || !moduleHasPlan) {
    return `/settings/subscription/addons?module=${encodeURIComponent(moduleName)}`;
  }

  return null;
};

const ProtectedFeatureRoute = ({
  component: Component,
  featureName,
  ...routeProps
}) => {
  const { isLoggedIn } = useUser();
  const feature = useFeature(featureName);
  const canAccess = !(feature.name && (!feature.enabled || !feature.canAccess));

  if (isLoggedIn && !canAccess) {
    return <Redirect to="/" />;
  }

  return <ProtectedOrganisationRoute component={Component} {...routeProps} />;
};

const ProtectedModuleRoute = ({
  component: Component,
  moduleIdentifier,
  ...routeProps
}) => {
  const { isLoggedIn } = useUser();
  const { hasModule, loading } = useModuleSubscription(moduleIdentifier);

  if (isLoggedIn && loading) {
    return <LoadingMessage center />;
  }

  if (isLoggedIn && !hasModule) {
    return <Redirect to="/" />;
  }

  return <ProtectedOrganisationRoute component={Component} {...routeProps} />;
};

const ProtectedOrganisationRoute = ({
  component: Component,
  moduleName,
  ...routeProps
}) => {
  const { isLoggedIn } = useUser();
  const organisations = useUserOrganisations();
  const { _id } = useOrganisation();
  const ShortCode = useShortCode();
  const location = useLocation();
  const pendingOrgShortCode = new URLSearchParams(location.search).get(
    "organisation",
  );

  const { data: subscriptionData } = useFetch(
    ShortCode && isLoggedIn ? SettingsService.getModules : noop,
    ShortCode,
    {
      suspense: false,
    },
  );

  const history = useHistory();

  useUpdateEffect(
    (didUpdate) => {
      if (!ShortCode || !didUpdate) return;
      history.push("/");
    },
    [ShortCode],
  );

  useEffect(() => {
    if (moduleName) return;

    const moduleSubscriptions = getModuleSubscriptions(subscriptionData);

    if (moduleSubscriptions.length > 0) {
      const allExpiredTrials = moduleSubscriptions.every((module) => {
        if (module?.isActive) return false;
        if (!module?.trialEndsAt) return false;
        return new Date(module.trialEndsAt) < new Date();
      });

      if (allExpiredTrials) {
        history.push("/connect");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscriptionData, moduleName]);
  const moduleAddonsPath = getModuleAddonsRedirectPath({
    moduleName,
    subscriptionData,
  });

  return (
    <ProtectedRoute
      render={(props) => {
        if (!ShortCode && !isLoggedIn) return <Redirect to="/login" />;

        if (
          !ShortCode &&
          !pendingOrgShortCode &&
          organisations &&
          !organisations.length &&
          !_id
        ) {
          return <Redirect to="/connect" />;
        }

        if (moduleAddonsPath) {
          return <Redirect to={moduleAddonsPath} />;
        }

        return _id ? <Component {...props} /> : <LoadingMessage center />;
      }}
      {...routeProps}
    />
  );
};

const ProtectedRoutes = ({
  component: Component,
  featureName,
  moduleIdentifier,
  ...routeProps
}) => {
  const { isLoggedIn } = useUser();
  const shortCode = useShortCode();

  if (moduleIdentifier && shortCode && isLoggedIn) {
    return (
      <ProtectedModuleRoute
        component={Component}
        moduleIdentifier={moduleIdentifier}
        {...routeProps}
      />
    );
  }

  if (featureName && shortCode && isLoggedIn) {
    return (
      <ProtectedFeatureRoute
        component={Component}
        featureName={featureName}
        {...routeProps}
      />
    );
  }

  return <ProtectedOrganisationRoute component={Component} {...routeProps} />;
};

ProtectedOrganisationRoute.propTypes = {
  component: elementType.isRequired,
  moduleName: string,
};

export default ProtectedRoutes;
